import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, animate } from "framer-motion";
import {
  ArrowRight,
  Heart,
  Users,
  HandHeart,
  Footprints,
  ShieldCheck,
  Building2,
  Globe,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { PUBLIC_ABOUT } from "../constants/testIds";

function Counter({ to, suffix = "", duration = 1.4 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration,
      ease: "easeOut",
      onUpdate: (v) => setValue(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to, duration]);
  const formatted = value >= 1000 ? value.toLocaleString() : value.toString();
  return (
    <span ref={ref}>
      {formatted}
      {suffix}
    </span>
  );
}

const missionItems = [
  { icon: Users, title: "WE CONNECT", desc: "We connect people with others who truly understand." },
  { icon: HandHeart, title: "WE SUPPORT", desc: "Practical help, resources and specialist support." },
  { icon: Footprints, title: "WE WALK TOGETHER", desc: "We walk beside you through every step of your cancer journey." },
  { icon: ShieldCheck, title: "WE'RE HERE", desc: "A safe space. A trusted place. Always by your side." },
];

const stats = [
  { icon: Users, value: 120, suffix: "K+", label: "People Supported", color: "#2D9B4E" },
  { icon: HandHeart, value: 9500, suffix: "+", label: "Trained Volunteers", color: "#1A5FAB" },
  { icon: Building2, value: 320, suffix: "+", label: "Hospital Trust Partners", color: "#2D9B4E" },
  { icon: Globe, value: 12, suffix: "", label: "Countries. One Mission.", color: "#1A5FAB" },
];

export default function About() {
  return (
    <div data-testid={PUBLIC_ABOUT.page} className="bg-white">
      <Header />
      <main id="main-content">
        {/* Hero */}
        <section data-testid={PUBLIC_ABOUT.hero} className="pt-28 lg:pt-32 pb-16 lg:pb-24 bg-white">
          <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-12">
            <p className="btc-eyebrow mb-4">ABOUT BONDED THRU CANCER</p>
            <h1 className="font-display font-extrabold tracking-tight leading-[1.05] text-[40px] sm:text-[52px] lg:text-[64px] text-[#0B132B] mb-6">
              A community <span className="text-[#1A5FAB]">bonded thru cancer.</span>
            </h1>
            <div className="h-[3px] w-16 bg-[#2D9B4E] rounded-full mb-6" />
            <p className="text-lg sm:text-xl text-[#4B5563] leading-relaxed max-w-3xl">
              Bonded Thru Cancer was founded on a single belief: no one should face cancer alone.
              We exist to bring patients, survivors, carers, volunteers, hospital trusts and support
              services into one connected community — so that every person touched by cancer has
              somewhere real to turn.
            </p>
          </div>
        </section>

        {/* Why BTC exists */}
        <section className="bg-[#F8F9FB] py-20 lg:py-28">
          <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div>
              <p className="btc-eyebrow mb-4">WHY WE EXIST</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#0B132B] tracking-tight leading-tight mb-6">
                Cancer is not a journey <br />
                <span className="text-[#23803F]">meant to be walked alone.</span>
              </h2>
            </div>
            <div className="space-y-5 text-[#4B5563] text-base sm:text-[17px] leading-relaxed">
              <p>
                A diagnosis reshapes daily life for the patient — and for the family, friends and
                carers who love them. Even in world-class hospitals, the moments in between
                appointments can feel isolating. BTC is built for those moments.
              </p>
              <p>
                We do not replace clinical care. We stand beside it — with human connection,
                trusted resources and a community that understands, because it has been there.
              </p>
              <p>
                Founded in the United Kingdom and built for patients everywhere, BTC brings the
                cancer community into one place: patient-first, provider-friendly, and rooted in
                dignity.
              </p>
            </div>
          </div>
        </section>

        {/* Mission strip */}
        <section data-testid={PUBLIC_ABOUT.mission} className="bg-[#1F5D5B] text-white">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 py-16 lg:py-20">
            <div className="max-w-2xl mb-12">
              <p className="text-[#8CD9AE] text-xs font-bold uppercase tracking-[0.2em] mb-3">
                OUR MISSION
              </p>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                Four commitments. One community.
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10 divide-y sm:divide-y-0 sm:divide-x divide-white/15">
              {missionItems.map((it, i) => (
                <motion.div
                  key={it.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: i * 0.07 }}
                  className={`flex items-start gap-5 ${i > 0 ? "sm:pl-8" : ""} pt-8 sm:pt-0`}
                >
                  <div className="shrink-0 w-14 h-14 rounded-full border-2 border-white/70 flex items-center justify-center">
                    <it.icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-bold text-[15px] tracking-[0.06em] mb-2">{it.title}</h3>
                    <p className="text-white/85 text-[14px] leading-relaxed">{it.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Impact */}
        <section data-testid={PUBLIC_ABOUT.impact} className="bg-white py-20 lg:py-28">
          <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-12">
            <div className="max-w-2xl mb-14">
              <p className="btc-eyebrow mb-4">OUR IMPACT</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#0B132B] tracking-tight leading-tight">
                Numbers that reflect <br />
                <span className="text-[#1A5FAB]">real people, real stories.</span>
              </h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((s, i) => (
                <motion.div
                  key={s.label}
                  data-testid={PUBLIC_ABOUT.impactStat}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.07 }}
                  className="flex items-start gap-4"
                >
                  <div
                    className="shrink-0 w-12 h-12 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${s.color}14` }}
                  >
                    <s.icon className="w-6 h-6" strokeWidth={1.5} style={{ color: s.color }} />
                  </div>
                  <div>
                    <div className="text-3xl lg:text-4xl font-extrabold text-[#0B132B] leading-none tracking-tight">
                      <Counter to={s.value} suffix={s.suffix} />
                    </div>
                    <div className="text-sm text-[#4B5563] mt-2 leading-tight font-medium">
                      {s.label}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Philosophy */}
        <section data-testid={PUBLIC_ABOUT.philosophy} className="bg-[#F8F9FB] py-20 lg:py-28">
          <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-12 text-center">
            <p className="btc-eyebrow mb-4">OUR PHILOSOPHY</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#0B132B] tracking-tight leading-tight mb-8">
              Connection is care.
            </h2>
            <p className="text-lg text-[#4B5563] leading-relaxed">
              We believe that lived experience is a form of expertise. That a message from someone
              who has been there can matter as much as any pamphlet. That community, when built with
              care, can be one of the most powerful forms of support in a person's journey. BTC is
              designed around that belief — quietly, patiently and with respect for every person it
              serves.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section data-testid={PUBLIC_ABOUT.cta} className="bg-white py-20 lg:py-24">
          <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-12 text-center">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#0B132B] tracking-tight leading-tight mb-6">
              Ready to be part of the community?
            </h2>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/support" className="btc-btn-primary justify-center">
                GET SUPPORT
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/get-involved" className="btc-btn-outline justify-center">
                GET INVOLVED
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
